import React, { lazy, Suspense, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './styles/cyberpunk.css';

import AnnouncementBanner from './components/AnnouncementBanner';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FabMenu from './components/FabMenu';
import { trackPageView, trackEvent } from './utils/pixel';

/* ─── Lazy-loaded pages (code splitting) ───
   After a deploy, a tab opened before it still points at the old chunk
   names, which no longer exist ("ChunkLoadError" / blank screen). Reload the
   page once to pick up the new build; the guard stops a reload loop. */
function lazyPage(load) {
  return lazy(() => load().then((m) => {
    try { sessionStorage.removeItem('wpxg-chunk-reload'); } catch (_) { /* storage blocked */ }
    return m;
  }).catch((err) => {
    const isChunk = err && (err.name === 'ChunkLoadError' || /Loading (CSS )?chunk/i.test(String(err.message)));
    let reloaded = false;
    try { reloaded = sessionStorage.getItem('wpxg-chunk-reload') === '1'; } catch (_) { reloaded = true; }
    if (isChunk && !reloaded) {
      try { sessionStorage.setItem('wpxg-chunk-reload', '1'); } catch (_) { /* storage blocked */ }
      window.location.reload();
      return new Promise(() => {});   // keep the loader up while reloading
    }
    throw err;
  }));
}
const HomePage = lazyPage(() => import('./pages/HomePage'));
const GamesPage = lazyPage(() => import('./pages/GamesPage'));
const PricingPage = lazyPage(() => import('./pages/PricingPage'));
const GiftCardsPage = lazyPage(() => import('./pages/GiftCardsPage'));
const FoodPage = lazyPage(() => import('./pages/FoodPage'));
const TournamentsPage = lazyPage(() => import('./pages/TournamentsPage'));
const ContactPage = lazyPage(() => import('./pages/ContactPage'));
const FAQPage = lazyPage(() => import('./pages/FAQPage'));
const PrivacyPage = lazyPage(() => import('./pages/PrivacyPage'));
const PartyPackagesPage = lazyPage(() => import('./pages/PartyPackagesPage'));
const PartyInvitePage = lazyPage(() => import('./pages/PartyInvitePage'));
const PartyTermsPage = lazyPage(() => import('./pages/PartyTermsPage'));
const DayPassTermsPage = lazyPage(() => import('./pages/DayPassTermsPage'));
const PartyBalancePage = lazyPage(() => import('./pages/PartyBalancePage'));
const PartyFoodPage = lazyPage(() => import('./pages/PartyFoodPage'));
const PrivateEventPage = lazyPage(() => import('./pages/PrivateEventPage'));
const PrivateQuotePage = lazyPage(() => import('./pages/PrivateQuotePage'));
const PrivateTermsPage = lazyPage(() => import('./pages/PrivateTermsPage'));
const BlogPage = lazyPage(() => import('./pages/BlogPage'));
const GalleryPage = lazyPage(() => import('./pages/GalleryPage'));
const ChampionshipsPage = lazyPage(() => import('./pages/ChampionshipsPage'));
const SOPPage = lazyPage(() => import('./pages/SOPPage'));
const SummerDayPassPage = lazyPage(() => import('./pages/SummerDayPassPage'));
const JobsPage = lazyPage(() => import('./pages/JobsPage'));
const SummerDayPassConfirmationPage = lazyPage(() => import('./pages/SummerDayPassConfirmationPage'));
const NotFoundPage = lazyPage(() => import('./pages/NotFoundPage'));


function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/* Meta Pixel — PageView on route change + ViewContent on commercial pages.
   The initial PageView fires from the base snippet in index.html, so the
   first render is skipped for PageView (but not ViewContent). */
/* Listing pages show several products at different prices, so there is no
   single honest `value` to report — Meta treats these as product_group and
   doesn't expect one. Pages with one definite price fire ViewContent
   themselves once that price has loaded (see SummerDayPassPage), which is why
   /summer-pass is absent here. */
const VIEW_CONTENT_PAGES = {
  '/gift-cards': { name: 'Gift Cards', type: 'product_group' },
  '/party': { name: 'Event Packages', type: 'product_group' },
  '/private-event': { name: 'Private Events', type: 'product_group' },
  '/pricing': { name: 'Credit Packs', type: 'product_group' },
  '/games': { name: 'Game Library', type: 'product_group' },
  '/game-day-grub': { name: 'Game Day Grub', type: 'product_group' },
  '/events': { name: 'Events', type: 'product_group' },
  '/championships': { name: 'Championships', type: 'product_group' },
};

function PixelPageViews() {
  const { pathname } = useLocation();
  const first = React.useRef(true);

  React.useEffect(() => {
    if (first.current) {
      first.current = false;      // base snippet already sent this PageView
    } else {
      trackPageView();
    }

    const vc = VIEW_CONTENT_PAGES[pathname.replace(/\/+$/, '') || '/'];
    if (vc) {
      trackEvent('ViewContent', {
        content_name: vc.name,
        content_type: vc.type,
        content_category: 'Whapaxx Gaming',
        currency: 'BBD',
      });
    }
  }, [pathname]);

  return null;
}

/* Minimal loading state that matches the dark theme */
function PageLoader() {
  return (
    <div style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        width: 40,
        height: 40,
        border: '3px solid rgba(18, 208, 255, 0.2)',
        borderTop: '3px solid #12d0ff',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

/* Routes that render without the site header, nav, and footer */
const CHROMELESS_ROUTES = ['/sop'];

function AppShell() {
  const { pathname } = useLocation();
  const [bannerHeight, setBannerHeight] = useState(0);
  const hideChrome = CHROMELESS_ROUTES.includes(pathname);

  return (
    <div className="scanlines">
      <div className="cyber-grid" />
      {!hideChrome && <AnnouncementBanner onHeightChange={setBannerHeight} />}
      {!hideChrome && <Navbar bannerOffset={bannerHeight} />}
      <ScrollToTop />
      <PixelPageViews />
      <main style={{ minHeight: '100vh', paddingTop: !hideChrome && bannerHeight > 0 ? bannerHeight : 0 }}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/games" element={<GamesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/gift-cards" element={<GiftCardsPage />} />
            <Route path="/game-day-grub" element={<FoodPage />} />
            <Route path="/events" element={<TournamentsPage />} />
            {/* Legacy paths — keep old links/QR codes working */}
            <Route path="/tournaments" element={<Navigate to="/events" replace />} />
            <Route path="/food" element={<Navigate to="/game-day-grub" replace />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/party" element={<PartyPackagesPage />} />
            <Route path="/party/terms" element={<PartyTermsPage />} />
            <Route path="/party/pay/:token" element={<PartyBalancePage />} />
            <Route path="/party/manage/:token" element={<PartyFoodPage />} />
            <Route path="/party/:slug" element={<PartyInvitePage />} />
            <Route path="/private-event" element={<PrivateEventPage />} />
            <Route path="/private-event/terms" element={<PrivateTermsPage />} />
            <Route path="/private-event/quote/:token" element={<PrivateQuotePage />} />
            <Route path="/party-packages" element={<Navigate to="/party" replace />} />
            <Route path="/bookings" element={<Navigate to="/party" replace />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:slug" element={<BlogPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/championships" element={<ChampionshipsPage />} />
            <Route path="/sop" element={<SOPPage />} />
            {/* The day pass, reused each school break. /daypass is its address;
                /day-pass and /summer-pass still work for links and emails already out there. */}
            <Route path="/daypass" element={<SummerDayPassPage />} />
            <Route path="/daypass/confirmation" element={<SummerDayPassConfirmationPage />} />
            <Route path="/daypass/terms" element={<DayPassTermsPage />} />
            <Route path="/day-pass" element={<SummerDayPassPage />} />
            <Route path="/day-pass/confirmation" element={<SummerDayPassConfirmationPage />} />
            <Route path="/summer-pass" element={<SummerDayPassPage />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/summer-pass/confirmation" element={<SummerDayPassConfirmationPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>
      {!hideChrome && <Footer />}
      {!hideChrome && <FabMenu />}
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
